import React from 'react'
import { FastField } from 'formik'
import { Box, Typography } from '@mui/material'
import { CustomTextField } from 'src/form'

import CustomChipSelect from 'src/form/CustomChipSelect'
import { Section, SubSection } from '../common/Section'

export function MentalStatusExaminationForm() {
  return (
    <Section title="Mental Status Examination">
      <SubSection>
        <Box>
          <Typography variant="h6" color="textSecondary">
            Observations
          </Typography>
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.APPEARANCE"
            label="Appearance"
            options={[
              { label: 'Neat', value: 'NEAT' },
              { label: 'Disheveled', value: 'DISHEVELED' },
              { label: 'Inappropriate', value: 'INAPPROPRIATE' },
              { label: 'Bizarre', value: 'BIZARRE' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.SPEECH"
            label="Speech"
            options={[
              { label: 'Normal', value: 'NORMAL' },
              { label: 'Tangential', value: 'TANGENTIAL' },
              { label: 'Pressured', value: 'PRESSURED' },
              { label: 'Impoverished', value: 'IMPOVERISHED' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.EYE_CONTACT"
            label="Eye contact"
            options={[
              { label: 'Normal', value: 'NORMAL' },
              { label: 'Intense', value: 'INTENSE' },
              { label: 'Avoidant', value: 'AVOIDANT' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.MOTOR_ACTIVITY"
            label="Motor activity"
            options={[
              { label: 'Normal', value: 'NORMAL' },
              { label: 'Restless', value: 'RESTLESS' },
              { label: 'Tics', value: 'TICS' },
              { label: 'Slowed', value: 'SLOWED' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.AFFECT"
            label="Affect"
            options={[
              { label: 'Full', value: 'FULL' },
              { label: 'Constrained', value: 'CONSTRAINED' },
              { label: 'Flat', value: 'FLAT' },
              { label: 'Labile', value: 'LABILE' },
              { label: 'Other', value: 'OTHER' },
            ]}
          />
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Mood
          </Typography>
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.MOOD"
            options={[
              { label: 'Euthymic', value: 'EUTHYMIC' },
              { label: 'Anxious', value: 'ANXIOUS' },
              { label: 'Angry', value: 'ANGRY' },
              { label: 'Depressed', value: 'DEPRESSED' },
              { label: 'Euphoric', value: 'EUPHORIC' },
              { label: 'Irritable', value: 'IRRITABLE' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Cognition
          </Typography>
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.ORIENTATION_IMPAIRMENT"
            label="Orientation impairment"
            options={[
              { label: 'None', value: 'NONE' },
              { label: 'Place', value: 'PLACE' },
              { label: 'Object', value: 'OBJECT' },
              { label: 'Person', value: 'PERSON' },
              { label: 'Time', value: 'TIME' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.MEMORY_IMPAIRMENT"
            label="Memory impairment"
            options={[
              { label: 'None', value: 'NONE' },
              { label: 'Short term', value: 'SHORT_TERM' },
              { label: 'Long term', value: 'LONG_TERM' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.ATTENTION"
            label="Attention"
            options={[
              { label: 'Normal', value: 'NORMAL' },
              { label: 'Distracted', value: 'DISTRACTED' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Perception
          </Typography>
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.HALLUCINATIONS"
            label="Hallucinations"
            options={[
              { label: 'None', value: 'NONE' },
              { label: 'Auditory', value: 'AUDITORY' },
              { label: 'Visual', value: 'VISUAL' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.DELUSIONS"
            label="Delusions"
            options={[
              { label: 'None', value: 'NONE' },
              { label: 'Grandiose', value: 'GRANDIOSE' },
              { label: 'Paranoid', value: 'PARANOID' },
              { label: 'Religious', value: 'RELIGIOUS' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Behavior
          </Typography>
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.BEHAVIOR"
            options={[
              { label: 'Cooperative', value: 'COOPERATIVE' },
              { label: 'Guarded', value: 'GUARDED' },
              { label: 'Hyperactive', value: 'HYPERACTIVE' },
              { label: 'Agitated', value: 'AGITATED' },
              { label: 'Paranoid', value: 'PARANOID' },
              { label: 'Stereotyped', value: 'STEREOTYPED' },
              { label: 'Aggressive', value: 'AGGRESSIVE' },
              { label: 'Bizarre', value: 'BIZARRE' },
              { label: 'Withdrawn', value: 'WITHDRAWN' },
              { label: 'Other', value: 'OTHER' },
            ]}
            fullWidth
          />
        </Box>

        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            Insight / Judgment
          </Typography>
          <FastField
            component={CustomChipSelect}
            name="MENTAL_STATUS_EXAMINATION.INSIGHT_JUDGMENT"
            options={[
              { label: 'Good', value: 'GOOD' },
              { label: 'Fair', value: 'FAIR' },
              { label: 'Inappropriate', value: 'INAPPROPRIATE' },
            ]}
            fullWidth
          />
        </Box>

        <FastField
          component={CustomTextField}
          name="MENTAL_STATUS_EXAMINATION.ADDITIONAL_NOTES"
          label="Additional notes"
          fullWidth
          multiline
          minRows={4}
        />
      </SubSection>
    </Section>
  )
}
